import React, {useEffect, useRef, useState} from 'react';
import OeTip from './OeTip';
import Swatch from './Swatch';
import {searchArticles} from './api';

/**
 * Add-Zeile unter der Positionstabelle (Original `#addrow` HTML 720–731 und
 * `addPos` 5067).
 *
 * Bewusst wie im Original: Hinzugefügt wird **nur** nach Klick auf einen
 * Vorschlag. Ein blinder Erst-Treffer aus der Suche würde bei ähnlichen
 * Artikelnummern die falsche Position in den Auftrag setzen.
 *
 * @param {string} mode - 'order' | 'shipment'
 * @param {boolean} canAdd - liefert der Beleg das Hinzufügen überhaupt?
 * @param {boolean} editable
 * @param {{tone: string, text: string}|null} status
 * @param {(status: {tone: string, text: string}|null) => void} onStatus
 * @param {(article: {id, articleNumber, name}, qty: string) => void} onAdd
 */
export default function AddPositionRow({mode, canAdd, editable, status, onStatus, onAdd}) {
    const [query, setQuery] = useState('');
    const [qty, setQty] = useState('1');
    const [list, setList] = useState(null);
    const [selected, setSelected] = useState(null);
    const wrapRef = useRef(null);
    const qtyRef = useRef(null);
    const timerRef = useRef(null);

    // Vorschläge ab zwei Zeichen mit 300 ms Verzögerung — so entsteht beim
    // Tippen nicht je Anschlag ein weclapp-Abruf.
    useEffect(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        const q = query.trim();
        if (q.length < 2 || selected) {
            setList(null);
            return undefined;
        }
        let abgebrochen = false;
        timerRef.current = setTimeout(async () => {
            try {
                const data = await searchArticles(q);
                if (!abgebrochen) {
                    setList(data.result || []);
                }
            } catch (e) {
                // Die Suche scheitert still — der Bearbeiter tippt weiter.
                if (!abgebrochen) {
                    setList([]);
                }
            }
        }, 300);
        return () => {
            abgebrochen = true;
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [query, selected]);

    // Klick außerhalb schließt die Vorschlagsliste (Original: Dokument-Klick).
    useEffect(() => {
        if (list === null) {
            return undefined;
        }
        const zu = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setList(null);
            }
        };
        document.addEventListener('mousedown', zu);
        return () => document.removeEventListener('mousedown', zu);
    }, [list]);

    if (mode !== 'order' || canAdd === false) {
        return (
            <div className="muted" style={{marginTop: 10}}>
                Positionen können am Lieferschein nicht hinzugefügt werden (nur über den Auftrag) — Mengen
                reduzieren und Positionen entfernen geht.
            </div>
        );
    }

    if (!editable) {
        return null;
    }

    const setzeArtikel = (a) => {
        setSelected({id: String(a.id), articleNumber: a.articleNumber, name: a.name});
        setQuery(`${a.articleNumber} · ${a.name}`);
        setList(null);
        onStatus(null);
        window.setTimeout(() => qtyRef.current?.focus(), 0);
    };

    const hinzufuegen = () => {
        if (!selected) {
            onStatus({tone: 'err', text: 'erst Artikel aus der Liste wählen'});
            return;
        }
        onAdd(selected, qty.trim() || '1');
        setSelected(null);
        setQuery('');
        setQty('1');
        setList(null);
    };

    return (
        <>
            <div className="addrow">
                <span className="sugg" ref={wrapRef}>
                    <OeTip tip="Artikelnummer oder Name eingeben. Ab zwei Zeichen erscheint eine Vorschlagsliste; hinzugefügt wird nur ein Artikel, den du dort anklickst.">
                        <input
                            style={{width: 260}}
                            placeholder="Artikel-Nr oder Name suchen…"
                            aria-label="Artikel suchen"
                            autoComplete="off"
                            value={query}
                            onChange={(e) => {
                                setQuery(e.target.value);
                                setSelected(null);
                                onStatus(null);
                            }}
                        />
                    </OeTip>
                    {list !== null ? (
                        <div className="sugg-list show" role="listbox" aria-label="Artikelvorschläge">
                            {list.length ? list.map((a) => (
                                <div
                                    key={a.id}
                                    role="option"
                                    aria-selected="false"
                                    tabIndex={0}
                                    onClick={() => setzeArtikel(a)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            setzeArtikel(a);
                                        }
                                    }}
                                >
                                    <Swatch title={a.name}/>
                                    <b>{a.articleNumber}</b> · {a.name}
                                </div>
                            )) : <div className="muted">kein Artikel gefunden</div>}
                        </div>
                    ) : null}
                </span>

                <OeTip tip="Menge der neuen Position. Den Preis setzt weclapp beim Speichern aus dem Vertriebsweg.">
                    <input
                        className="q"
                        style={{width: 80}}
                        placeholder="Menge"
                        aria-label="Menge der neuen Position"
                        inputMode="decimal"
                        autoComplete="off"
                        ref={qtyRef}
                        value={qty}
                        onChange={(e) => setQty(e.target.value)}
                    />
                </OeTip>

                <OeTip tip={'Setzt die Position unten in die Tabelle — grün markiert und noch nicht gespeichert. '
                    + 'Erst „Speichern" legt sie in weclapp an.'}>
                    <button
                        type="button"
                        id="oe-addBtn"
                        disabled={!selected}
                        onClick={hinzufuegen}
                    >
                        + Position
                    </button>
                </OeTip>

                {status ? <span className={`status ${status.tone}`}>{status.text}</span> : null}
            </div>
        </>
    );
}
